// 案件ダッシュボード（核心）— タイムライン ＋ AI集約セクション
//
// Claude Design プロトタイプ (_management/designs/ui_prototype/app/Dashboard.jsx) の
// 2カラム構造をそのまま再現する:
//   左カラム = 縦線付きタイムライン（meetings 直読）
//   右カラム = 案件サマリ / 決定事項 / 変更点候補 / キーパーソン
// AI 派生セクション（サマリ・決定事項・キーパーソン）は projects/{id}.dashboard を読む
// （computeProjectDashboard が生成 / S-051-2a）。stale 判定 + オンデマンド再計算 = S-051-2b。
// 派生セクション + プランロック = S-051-2c。変更点候補は Phase 3（S-051-3a）のためロック表示。
//
// S-055-1（DEC-053 案B）: タイムライン/AIセクション/サマリ/決定事項/キーパーソンは
// components/DashboardSections.tsx へ抽出した純表示部品を流用する（会社別詳細と共通化）。
// 本ページはデータ取得層（meetings/issues/project doc 購読・chat 呼出）に専念する。
import React, { useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  PlanType, planHasAiFeatures, planHasFullTimeline, webDisplayPlan,
} from '../plan';
import { useFreeTimelineViewLimit } from '../remoteConfig';
import {
  Btn, Card, Icon, LockVeil, Pill, SectionLabel,
} from '../components/primitives';
import {
  Timeline, AISection, SummaryCard, Decisions, KeyPersons,
  DashboardDecision, DashboardKeyPerson, toDate,
} from '../components/DashboardSections';
import ChatPanel from '../components/ChatPanel';
import { MeetingDecision } from '../projectRollup';

interface Project {
  id: string;
  name: string;
  description?: string;
  stage?: string;
  client?: string;
  updatedAt?: any;
}

interface Meeting {
  id: string;
  title: string;
  recordedAt?: any;
  duration?: number;
  status?: string;
  deletedAt?: any;
  channel?: string;
  excerpt?: string;
  summary?: string;
  participants?: string[];
  // S-058-3: 会議タイプ（DEC-054 MEETINGCLASS）と per-meeting 決定（Phase1 / S-057 で保存）。
  // 既存購読は ...d.data() で取得済みのため型追加のみで読取が通る。欠損は安全側デフォルト。
  category?: string; // 'sales' | 'internal' | null
  decisions?: MeetingDecision[];
}

// projects/{id}.dashboard（computeProjectDashboard の派生キャッシュ）
interface DashboardData {
  summaryText?: string;
  decisions?: DashboardDecision[];
  keyPersons?: DashboardKeyPerson[];
  lastComputedAt?: any;
  latestMeetingRecordedAt?: any;
  computedFromMeetingCount?: number;
  truncated?: boolean;
  stale?: boolean;
}

interface Props {
  user: User;
  project: Project;
  onBack: () => void;
  // S-058-7: 引用タップ時にハイライト対象の逐語（highlight）を任意で引き回す。
  onSelectMeeting: (meeting: Meeting, highlight?: string) => void;
  plan: PlanType;
  planLoading: boolean;
  // Web で議事録の中身を閲覧できないプラン（free / light / DEC-059）。
  // タイムラインの会議カードのプレビュー本文だけをぼかす（タイトル・日時は読める）。
  webMinutesLocked?: boolean;
}

const ProjectDashboardPage: React.FC<Props> = ({ project, onBack, onSelectMeeting, plan, planLoading, webMinutesLocked }) => {
  const [projectDoc, setProjectDoc] = useState<Project & { dashboard?: DashboardData }>(project);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  const [meetingsError, setMeetingsError] = useState<string | null>(null);

  const [offline, setOffline] = useState(!navigator.onLine);

  const [chatOpen, setChatOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);

  // 案件ドキュメント購読（dashboard 派生キャッシュ + stage/client などのメタ）。
  useEffect(() => {
    const ref = doc(db, 'projects', project.id);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setProjectDoc({ id: snap.id, ...(snap.data() as any) });
        }
      },
      (e: any) => {
        console.error('[ProjectDashboardPage] failed to subscribe project doc:', e);
      }
    );
    return unsubscribe;
  }, [project.id]);

  // 会議タイムライン購読（recordedAt 降順）。
  useEffect(() => {
    const q = query(
      collection(db, 'projects', project.id, 'meetings'),
      orderBy('recordedAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as Meeting))
          .filter((m) => m.deletedAt == null);
        setMeetings(data);
        setMeetingsError(null);
        setMeetingsLoading(false);
      },
      (e: any) => {
        console.error('[ProjectDashboardPage] failed to subscribe meetings:', e);
        const detail = e?.code ? `${e.code}: ${e.message ?? e}` : (e?.message ?? String(e));
        setMeetingsError(`タイムラインの取得に失敗しました (${detail})`);
        setMeetingsLoading(false);
      }
    );
    return unsubscribe;
  }, [project.id]);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ─── プラン出し分け（S-051-1h）。解決前は楽観的にアンロック・全件。 ───
  // 無料の閲覧件数は RemoteConfig で動的制御（既定2件 / DEC-049 更新）。
  const freeViewLimit = useFreeTimelineViewLimit();
  const aiUnlocked = planLoading || planHasAiFeatures(plan);
  // Web 表示では free を light と同一にする（webDisplayPlan）＝ free も全件表示（中身はモザイク）。
  const fullTimeline = planLoading || planHasFullTimeline(webDisplayPlan(plan));
  const hiddenMeetingCount = fullTimeline ? 0 : Math.max(0, meetings.length - freeViewLimit);
  const visibleMeetings = fullTimeline ? meetings : meetings.slice(0, freeViewLimit);

  const dashboard = projectDoc?.dashboard;
  const decisions = dashboard?.decisions ?? [];
  const keyPersons = dashboard?.keyPersons ?? [];
  const summaryText = dashboard?.summaryText ?? '';
  const hasDashboard = !!dashboard && (!!summaryText || decisions.length > 0 || keyPersons.length > 0);

  // stale 判定: 最新会議の recordedAt が dashboard.lastComputedAt より新しければ再計算が必要（S-051-2b）。
  const stale = useMemo(() => {
    if (!hasDashboard) return false;
    if (dashboard?.stale === true) return true;
    const latest = toDate(meetings[0]?.recordedAt);
    const computedAt = toDate(dashboard?.lastComputedAt);
    if (!latest || !computedAt) return false;
    return latest.getTime() > computedAt.getTime();
  }, [hasDashboard, dashboard, meetings]);

  const goPricing = () => { window.location.href = '/pricing'; };

  // 出典 meetingId → タイムラインの会議を開く（S-058-7: 引用の逐語を任意でハイライト指定）。
  const jump = (meetingId?: string, highlight?: string) => {
    if (!meetingId) return;
    const m = meetings.find((x) => x.id === meetingId);
    if (m) onSelectMeeting(m, highlight);
  };

  const openChat = () => {
    if (aiUnlocked) setChatOpen(true);
    else goPricing();
  };

  // オンデマンド再計算（standard 限定 / S-051-2b）。成功時は project doc 購読が dashboard を更新。
  const recompute = async () => {
    if (recomputing) return;
    setRecomputing(true);
    setRecomputeError(null);
    try {
      const fn = httpsCallable<{ projectId: string }, any>(functions, 'computeProjectDashboard');
      await fn({ projectId: project.id });
      // 成功後の dashboard 反映は project doc の onSnapshot が担う。
    } catch (e: any) {
      console.error('[ProjectDashboardPage] computeProjectDashboard failed:', e);
      const msg: string = e?.message || String(e);
      if (msg.includes('PLAN_REQUIRED_STANDARD')) {
        setRecomputeError('サマリの生成はスタンダードプランでご利用いただけます。');
      } else {
        setRecomputeError(`サマリの最新化に失敗しました: ${msg}`);
      }
    } finally {
      setRecomputing(false);
    }
  };

  const stage = projectDoc?.stage;
  const client = projectDoc?.client;

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 24px 96px' }}>
      {/* パンくず */}
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
      }}>
        <Icon name="arrow-left" size={16} />案件一覧
      </button>

      {offline && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 16,
          borderRadius: 'var(--r-2)', background: 'var(--cta-fill)', border: '1px solid var(--cta-line)',
          color: 'var(--amber-400)', font: 'var(--type-caption)',
        }}>
          <Icon name="wifi-off" size={15} />
          オフラインです。電波のある場所で再度開いてください（Web版はオフライン閲覧に非対応です）
        </div>
      )}

      {/* 案件ヘッダ */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          {(stage || client) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              {stage && <Pill kind="teal">{stage}</Pill>}
              {client && <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{client}</span>}
            </div>
          )}
          <h1 style={{ font: 'var(--type-h1)', color: 'var(--fg)' }}>{projectDoc?.name || project.name}</h1>
        </div>
        <Btn kind="accent" icon={aiUnlocked ? 'message-square-text' : 'lock'} onClick={openChat}
          title={aiUnlocked ? '' : 'スタンダードで利用できます'}>
          この案件にAIで聞く
        </Btn>
      </div>

      {/* 2カラム */}
      <div className="dash-grid">
        {/* 左: タイムライン */}
        <div>
          <Timeline
            meetings={visibleMeetings}
            loading={meetingsLoading}
            error={meetingsError}
            totalCount={meetings.length}
            onOpenMeeting={onSelectMeeting}
            lockExcerpt={webMinutesLocked}
          />
          {/* free プランのロック導線（直近 freeViewLimit 件のみ・既定2 / RC連動 / DEC-059） */}
          {hiddenMeetingCount > 0 && (
            <div style={{ position: 'relative', marginTop: 14 }}>
              <Card pad={18} style={{ borderStyle: 'dashed' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center',
                    background: 'var(--cta-fill)', color: 'var(--cta)',
                  }}>
                    <Icon name="lock" size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', marginBottom: 6 }}>
                      これ以前の{hiddenMeetingCount}件はライト以上で閲覧できます
                    </div>
                    <p style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)', lineHeight: 1.6, marginBottom: 12 }}>
                      無料プランは直近{freeViewLimit}件まで。ライト（¥480/月）で全件、スタンダード（¥980/月）でAIにも聞けます。
                    </p>
                    <Btn kind="cta" size="sm" icon="arrow-up-circle" onClick={goPricing}>プランを見る</Btn>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* 右: AI集約 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 案件サマリ + stale バナー */}
          <AISection locked={!aiUnlocked} onUpsell={goPricing} message="スタンダードで案件サマリを自動生成します">
            <SummaryCard
              hasDashboard={hasDashboard}
              summaryText={summaryText}
              computedAt={dashboard?.lastComputedAt}
              stale={stale}
              recomputing={recomputing}
              recomputeError={recomputeError}
              onRecompute={recompute}
              locked={!aiUnlocked}
            />
          </AISection>

          {/* 決定事項（AI） */}
          <AISection locked={!aiUnlocked} onUpsell={goPricing} message="スタンダードで決定事項を自動抽出します">
            <Decisions decisions={decisions} onJump={jump} />
          </AISection>

          {/* 変更点候補（Phase 3 / S-051-3a） — 近日対応プレースホルダ */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--r-3)' }}>
            <div style={{ filter: 'blur(7px) saturate(0.8)', pointerEvents: 'none', userSelect: 'none' }}>
              <ChangeAlertsPlaceholder />
            </div>
            <LockVeil icon="git-compare" message="過去の打合せとの変更点候補は近日対応予定です" />
          </div>

          {/* キーパーソン（AI） */}
          <AISection locked={!aiUnlocked} onUpsell={goPricing} message="スタンダードでキーパーソンを整理します">
            <KeyPersons persons={keyPersons} />
          </AISection>
        </div>
      </div>

      {/* フローティングチャット起動ボタン */}
      {!chatOpen && (
        <button onClick={openChat} style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 55,
          height: 52, padding: '0 20px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
          background: 'var(--cta)', color: 'var(--on-cta)', border: 'none',
          display: 'flex', alignItems: 'center', gap: 9, font: 'var(--type-ui)', fontWeight: 600,
          boxShadow: '0 12px 32px var(--cta-glow)',
        }}>
          <Icon name={aiUnlocked ? 'message-square-text' : 'lock'} size={19} stroke={2} />
          AIに聞く
        </button>
      )}

      <ChatPanel
        open={chatOpen && aiUnlocked}
        project={{ id: project.id, name: projectDoc?.name || project.name, meetingCount: meetings.length }}
        onClose={() => setChatOpen(false)}
        onJump={(mid) => { setChatOpen(false); jump(mid); }}
      />
    </div>
  );
};

// ─── 変更点候補（Phase 3 プレースホルダ） ───
const ChangeAlertsPlaceholder: React.FC = () => (
  <Card>
    <SectionLabel icon="git-compare">確認したい変更点</SectionLabel>
    <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: -8, marginBottom: 14, lineHeight: 1.5 }}>
      過去の打合せとの差分から、確認したいポイントの候補を挙げます。内容はご確認ください。
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[0, 1].map((i) => (
        <div key={i} style={{
          padding: 16, borderRadius: 'var(--r-2)', background: 'var(--bg-hover)', border: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Icon name="alert-circle" size={15} style={{ color: 'var(--amber-400)' }} />
            <span style={{ font: '500 14px/1.3 var(--font-sans)', color: 'var(--fg)' }}>変更点トピック</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
            <div style={{ flex: 1, padding: 10, borderRadius: 'var(--r-2)', background: 'var(--danger-fill)', border: '1px solid var(--danger-fill-strong)' }}>
              <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginBottom: 4 }}>変更前</div>
              <div style={{ font: 'var(--type-caption)', fontSize: 12.5, color: 'var(--fg-secondary)', lineHeight: 1.5 }}>　</div>
            </div>
            <div style={{ display: 'grid', placeItems: 'center', color: 'var(--fg-tertiary)' }}><Icon name="arrow-right" size={16} /></div>
            <div style={{ flex: 1, padding: 10, borderRadius: 'var(--r-2)', background: 'var(--accent-fill-faint)', border: '1px solid var(--accent-fill-hover)' }}>
              <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginBottom: 4 }}>変更後</div>
              <div style={{ font: 'var(--type-caption)', fontSize: 12.5, color: 'var(--fg)', lineHeight: 1.5 }}>　</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

export default ProjectDashboardPage;
